// src/components/EmailSignup.jsx - ENHANCED: Subscription-aware response handling
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom'; 
import { CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { emailService } from '../lib/supabase';
import subscriptionService from '../services/subscriptionService';

// Import analytics functions
import { trackEvent, trackEmailSignup } from '../utils/analytics';

// 🆕 ENHANCED: Import subscription modal
import SubscriptionRequiredModal from './SubscriptionRequiredModal';

// 🆕 NEW: Import pass usage modal
// 1-29-26
import PassUsageModal from './PassUsageModal';

// Add state at top of component (around line 20)
function EmailSignup({ 
  title = "Join the Community", 
  subtitle = "Get priority access to events and exclusive updates",
  placeholder = "Enter your email address",
  buttonText = "Get Priority Access",
  source = "website",
  className = "",
  variant = "default",
  metadata = {},
  onSuccess,
  onReservationUpdate
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, payment_required
  const [message, setMessage] = useState('');
  const [isReactivated, setIsReactivated] = useState(false);
  
  // 🆕 ENHANCED: Add subscription modal state
  const [subscriptionModal, setSubscriptionModal] = useState({
    isOpen: false,
    data: null
  });

  // 1-29-26
   // 🆕 NEW: Add pass usage modal state
  const [passUsageModal, setPassUsageModal] = useState({
    isOpen: false,
    email: '',
    passCount: 0,
    eventSlug: '',
    eventTitle: '',
    eventDate: ''
  });
  //
  
  // 🆕 NEW: Payment-related state
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);

  // 1-29-26
  const [passActionLoading, setPassActionLoading] = useState(false);
  //

  const validateEmail = (email) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  // 🆕 NEW: Handle Stripe checkout for pay-per-event
  const handlePayPerEvent = async () => {
    if (!paymentInfo || !email) return;
    
    setStatus('loading');
    setMessage('Creating payment session...');
    
    try {
      // Store email for checkout process
      subscriptionService.storeCheckoutEmail(email);
      
      // Create checkout session for the specific event
      const result = await subscriptionService.createEventCheckoutSession(
        email, 
        paymentInfo.eventPrice, 
        paymentInfo.currency,
        metadata
      );
      
      if (result.success) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl;
      } else {
        setStatus('error');
        setMessage(result.error || 'Failed to create payment session');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Payment setup failed. Please try again.');
    }
  };

  // 🆕 NEW: Handle subscription signup
  const handleSubscriptionSignup = async () => {
    if (!email) return;
    
    setStatus('loading');
    setMessage('Redirecting to subscription...');
    
    try {
      subscriptionService.storeCheckoutEmail(email);
      
      // Redirect to subscription page with email pre-filled
      const subscriptionUrl = new URL('/subscription', window.location.origin);
      subscriptionUrl.searchParams.set('email', email);
      subscriptionUrl.searchParams.set('source', 'event-signup');
      
      // Add event context if available from modal
      if (subscriptionModal.data?.eventSlug) {
        subscriptionUrl.searchParams.set('event', subscriptionModal.data.eventSlug);
      }
      
      window.location.href = subscriptionUrl.toString();
    } catch (error) {
      setStatus('error');
      setMessage('Subscription redirect failed. Please try again.');
    }
  };

  // 🆕 NEW: Handle waitlist signup
  const handleWaitlistSignup = async () => {
    setStatus('loading');
    setMessage('Joining waitlist...');
    
    // Force waitlist by setting special metadata
    const waitlistMetadata = {
      ...metadata,
      force_waitlist: true,
      payment_declined: true,
      access_type: 'waitlist_no_payment',
      rsvp_status_override: 'waitlisted' // 🔥 CRITICAL: Edge function checks this
    };
    
    // Continue with original signup flow but force waitlist
    await handleSubmit(null, waitlistMetadata);
  };

  // 🆕 ENHANCED: Handle waitlist signup from modal
  const handleWaitlistFromModal = async () => {
    console.log('🎯 Waitlist button clicked from modal');
    
    // Close modal first
    setSubscriptionModal({ isOpen: false, data: null });
    
    setStatus('loading');
    setMessage('Joining waitlist...');
    
    // Force waitlist by setting special metadata
    const waitlistMetadata = {
      ...metadata,
      force_waitlist: true,
      payment_declined: true,
      access_type: 'waitlist_no_payment',
      rsvp_status_override: 'waitlisted' // 🔥 CRITICAL: Edge function checks this
    };
    
    console.log('📝 Submitting waitlist signup with metadata:', waitlistMetadata);
    
    // Continue with original signup flow but force waitlist
    await handleSubmit(null, waitlistMetadata);
  };

  // 🆕 NEW: Fallback email storage function
  const storeEmailForSubscription = (email) => {
    try {
      localStorage.setItem('connectco_checkout_email', email);
      console.log('📧 Email stored for subscription redirect:', email);
    } catch (error) {
      console.warn('⚠️ Could not store email in localStorage:', error);
    }
  };

// 1-29-26
// 🆕 NEW: Handle using an event pass
const handleUsePass = async () => {
  try {
    setPassActionLoading(true);
    console.log('🎫 Using event pass for:', passUsageModal);
    
    // Call the useEventPass service method
    const result = await subscriptionService.useEventPass(
      passUsageModal.email,
      passUsageModal.eventSlug,
      passUsageModal.eventTitle,
      passUsageModal.eventDate
    );
    
    if (result.success) {
      // Close the modal
      setPassUsageModal({
        isOpen: false,
        email: '',
        passCount: 0,
        eventSlug: '',
        eventTitle: '',
        eventDate: ''
      });
      
      // Now proceed with event registration using the same email
      setStatus('loading');
      setMessage('Registering you for the event...');
      
      // Add metadata flag to indicate pass was used
      const passMetadata = {
        ...metadata,
        used_event_pass: true,
        pass_id: result.pass?.id
      };
      
      // Submit email with pass metadata
      const registrationResult = await emailService.addEmail(
        passUsageModal.email.trim(), 
        source, 
        passMetadata
      );
      
      if (registrationResult.success) {
        setStatus('success');
        
        if (registrationResult.reservation) {
          const userStatus = registrationResult.reservation.rsvpStatus || 'confirmed';
          
          if (userStatus === 'waitlisted') {
            setMessage(`🎫 Pass used! You're on the waitlist at position #${registrationResult.reservation.waitlistPosition || '?'}`);
          } else {
            setMessage(`🎉 Pass used! You're confirmed for this event! Event details will be sent 24 hours beforehand.`);
          }
          
          if (onReservationUpdate) {
            onReservationUpdate({
              success: true,
              count: registrationResult.reservation.currentCount || 0,
              waitlistCount: registrationResult.reservation.waitlistCount || 0,
              capacity: registrationResult.reservation.capacity || 25,
              isFull: registrationResult.reservation.isFull || false,
              rsvpStatus: registrationResult.reservation.rsvpStatus || 'confirmed',
              userReserved: true,
              duplicate: false,
              waitlistPosition: registrationResult.reservation.waitlistPosition || null,
              eventSlug: registrationResult.reservation.eventSlug
            });
          }
        } else {
          setMessage('🎫 Pass used successfully! Check your email for event details.');
        }
        
        // Track pass usage
        await trackEvent('event_pass_used', {
          event_slug: passUsageModal.eventSlug,
          pass_count_remaining: passUsageModal.passCount - 1,
          signup_source: source
        });
        
        // Clear email
        setEmail('');
        
        if (onSuccess) {
          onSuccess({
            email: passUsageModal.email,
            reservation: registrationResult.reservation,
            usedPass: true
          });
        }
      } else {
        setStatus('error');
        setMessage(registrationResult.error || 'Registration failed after using pass');
      }
      
    } else {
      setStatus('error');
      setMessage(result.error || 'Failed to use pass');
    }
    
  } catch (error) {
    console.error('❌ Error using pass:', error);
    setStatus('error');
    setMessage('Failed to use pass. Please try again.');
  } finally {
    setPassActionLoading(false);
  }
};

// 🆕 NEW: Handle subscription redirect from pass modal
const handleBuySubscriptionFromPass = () => {
  console.log('💳 Redirecting to subscription from pass modal');
  
  // Close modal
  setPassUsageModal({
    isOpen: false,
    email: '',
    passCount: 0,
    eventSlug: '',
    eventTitle: '',
    eventDate: ''
  });
  
  // Store email
  subscriptionService.storeCheckoutEmail(passUsageModal.email);
  
  // Redirect to subscription page with context
  const subscriptionUrl = new URL('/subscription', window.location.origin);
  subscriptionUrl.searchParams.set('email', passUsageModal.email);
  subscriptionUrl.searchParams.set('source', 'event-pass-modal');
  subscriptionUrl.searchParams.set('event', passUsageModal.eventSlug);
  
  window.location.href = subscriptionUrl.toString();
};


// end of 1-29-26

const handleSubmit = async (e, overrideMetadata = null) => {
  if (e) e.preventDefault();

  if (!email.trim()) {
    setStatus('error');
    setMessage('Please enter your email address');
    return;
  }

  if (!validateEmail(email)) {
    setStatus('error');
    setMessage('Please enter a valid email address');
    return;
  }

  setStatus('loading');
  setMessage('');
  setIsReactivated(false);
  setShowPaymentOptions(false);

  // Track form start attempt
  await trackEvent('email_signup_attempt', {
    signup_source: source,
    form_variant: variant,
    page_location: window.location.pathname,
    ...metadata
  });

  // Use override metadata for waitlist flows
  const finalMetadata = overrideMetadata || {
    ...metadata,
    signup_page: window.location.pathname,
    campaign_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
    campaign_medium: new URLSearchParams(window.location.search).get('utm_medium') || 'organic',
    campaign_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || 'none',
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

 

 // 2-8-26 — Combined access check: subscription first, then event pass
  // ONLY runs for actual event registrations (has event_slug in metadata or source starts with "event-")
  // Newsletter signups, page-level CTAs, and other non-event sources skip this entirely.

  const isEventRegistration = !!(metadata.event_slug || source.startsWith('event-'));

  if (isEventRegistration) {

       // 🔥 NEW: Check if this event actually requires payment/subscription
    const eventRequiresPayment = metadata.event_payment_enabled === true && 
                                metadata.event_requires_subscription === true;
    if (eventRequiresPayment) {
        const eventSlugForCheck = metadata.event_slug || source.replace('event-', '');
        const accessCheck = await subscriptionService.checkEventAccess(email.trim(), eventSlugForCheck);

        console.log('🔐 Access check result:', {
          hasAccess: accessCheck.hasAccess,
          accessType: accessCheck.accessType,
          apiSuccess: accessCheck.success
        });

        if (accessCheck.hasAccess && accessCheck.accessType === 'event_pass') {
          console.log(`✅ Access via event pass (${accessCheck.event_pass?.available_count} available)`);

          setPassUsageModal({
            isOpen: true,
            email: email,
            passCount: accessCheck.event_pass?.available_count || 1,
            eventSlug: eventSlugForCheck,
            eventTitle: metadata.event_title || 'This Event',
            eventDate: metadata.event_date || new Date().toISOString()
          });

          setStatus('idle');
          return;
        }

        if (!accessCheck.hasAccess && accessCheck.accessType === 'none') {
          console.log('❌ No access: no subscription and no event pass');

          subscriptionService.storeCheckoutEmail(email);

          setSubscriptionModal({
            isOpen: true,
            data: {
              eventTitle: metadata.event_title || 'This Event',
              eventDate: metadata.event_date || new Date().toISOString(),
              eventSlug: eventSlugForCheck,
              hasSubscription: false,
              eligibilityStatus: 'no_subscription',
              blockingMessage: 'Subscription or event pass required to attend this event',
              suggestedAction: 'Subscribe to access all events, or purchase a single event pass',
              isFull: false,
              spotsAvailable: 0,
              currentCount: 0,
              waitlistCount: 0,
              capacity: metadata.event_capacity || 25,
              paymentOptions: {
                allowSubscription: true,
                allowPerEventPayment: metadata.event_per_event_price_cents > 0,
                allowWaitlist: true,
                perEventPriceCents: metadata.event_per_event_price_cents || null
              },
              plans: {
                monthly: { price: 210000, interval: 'month' },
                sixMonth: { price: 1134000, interval: '6_months', discount: 10 },
                yearly: { price: 2142000, interval: 'year', discount: 15 }
              }
            }
          });

          setStatus('idle');
          return;
        }
        console.log('✅ Proceeding to registration (access via subscription or fallback)');
    } else {
      console.log('🆓 Free event detected (paymentEnabled:', metadata.event_payment_enabled, 
                ', requiresSubscription:', metadata.event_requires_subscription, 
                ') — skipping access check, proceeding to addEmail');
    }

  } else {
    console.log('📧 Non-event signup — skipping access check, proceeding directly to addEmail');
  }
  // end of 2-8-26


  // Submit email to database
  const result = await emailService.addEmail(email.trim(), source, finalMetadata);


// 12-24-25
// 🔥 ENHANCED: Handle subscription requirement with detailed coverage information
if (result.requiresSubscription) {
  console.log('🚫 Subscription required - showing modal with coverage details');
  console.log('📊 Full result object:', result);
  
  // Store email safely
  if (typeof subscriptionService !== 'undefined' && subscriptionService.storeCheckoutEmail) {
    subscriptionService.storeCheckoutEmail(email);
  } else {
    storeEmailForSubscription(email);
  }
  
  // 🔥 CRITICAL: Extract subscription coverage information
  // The backend returns different structures depending on the error type
  let subscriptionData;
  
  if (result.subscriptionCoverage) {
    // NEW: Subscription exists but doesn't cover event date
    console.log('📅 Subscription coverage issue detected:', result.subscriptionCoverage);
    
    subscriptionData = {
      // Event information
      eventTitle: result.subscriptionCoverage.eventTitle || metadata.event_title || 'This Event',
      eventDate: result.subscriptionCoverage.eventDate || metadata.event_date,
      eventSlug: result.subscriptionCoverage.eventSlug || metadata.event_slug,
      
      // Subscription coverage details
      subscriptionEndDate: result.subscriptionCoverage.subscriptionEndDate,
      daysCoverage: result.subscriptionCoverage.daysCoverage,
      eligibilityStatus: result.subscriptionCoverage.eligibilityStatus,
      
      // Error context
      hasSubscription: result.hasSubscription || false,
      subscriptionExpiresBeforeEvent: result.subscriptionExpiresBeforeEvent || false,
      blockingMessage: result.message || 'Your subscription does not cover this event',
      suggestedAction: result.suggestedAction || 'Please extend your subscription to attend this event',
      
      // Capacity information
      isFull: result.subscriptionCoverage.isFull || false,
      spotsAvailable: result.subscriptionCoverage.spotsAvailable || 0,
      currentCount: result.currentCount || result.subscriptionCoverage.currentCount || 0,
      waitlistCount: result.subscriptionCoverage.waitlistCount || 0,
      capacity: result.subscriptionCoverage.capacity || metadata.event_capacity || 25,
      
      // Payment options
      paymentOptions: {
        allowSubscription: true,
        allowPerEventPayment: false, // Can't pay per event if subscription required
        allowWaitlist: true, // Still allow waitlist
        perEventPriceCents: null
      },
      
      // Subscription plans
      plans: {
        monthly: { price: 210000, interval: 'month' },
        sixMonth: { price: 1134000, interval: '6_months', discount: 10 },
        yearly: { price: 2142000, interval: 'year', discount: 15 }
      }
    };
    
  } else if (result.subscriptionRequired || result.eventInfo) {
    // EXISTING: No subscription at all
    console.log('❌ No active subscription detected');
    
    const eventInfo = result.subscriptionRequired || result.eventInfo;
    
    subscriptionData = {
      eventTitle: eventInfo.eventTitle || metadata.event_title || 'This Event',
      eventDate: eventInfo.eventDate || metadata.event_date,
      eventSlug: eventInfo.eventSlug || metadata.event_slug,
      
      // No subscription coverage info
      subscriptionEndDate: null,
      daysCoverage: null,
      eligibilityStatus: 'no_subscription',
      
      hasSubscription: false,
      subscriptionExpiresBeforeEvent: false,
      blockingMessage: 'Active subscription required for this event',
      suggestedAction: 'Subscribe to access this event and all future events',
      
      isFull: eventInfo.isFull || false,
      spotsAvailable: eventInfo.spotsAvailable || 0,
      currentCount: result.currentCount || eventInfo.currentCount || 0,
      waitlistCount: eventInfo.waitlistCount || 0,
      capacity: eventInfo.capacity || metadata.event_capacity || 25,
      
      paymentOptions: eventInfo.paymentOptions || {
        allowSubscription: true,
        allowPerEventPayment: metadata.event_per_event_price_cents > 0,
        allowWaitlist: true,
        perEventPriceCents: metadata.event_per_event_price_cents || null
      },
      
      plans: eventInfo.plans || {
        monthly: { price: 210000, interval: 'month' },
        sixMonth: { price: 1134000, interval: '6_months', discount: 10 },
        yearly: { price: 2142000, interval: 'year', discount: 15 }
      }
    };
    
  } else {
    // FALLBACK: Minimal data from metadata
    console.warn('⚠️ Limited subscription data, using metadata fallback');
    
    subscriptionData = {
      eventTitle: metadata.event_title || 'This Event',
      eventDate: metadata.event_date || new Date().toISOString(),
      eventSlug: metadata.event_slug || source.replace('event-', ''),
      
      subscriptionEndDate: null,
      daysCoverage: null,
      eligibilityStatus: 'unknown',
      
      hasSubscription: false,
      subscriptionExpiresBeforeEvent: false,
      blockingMessage: result.message || 'Subscription required',
      suggestedAction: 'Get a subscription to access this event',
      
      isFull: result.currentCount >= (metadata.event_capacity || 20),
      spotsAvailable: Math.max(0, (metadata.event_capacity || 20) - (result.currentCount || 0)),
      currentCount: result.currentCount || 0,
      waitlistCount: 0,
      capacity: metadata.event_capacity || 25,
      
      paymentOptions: {
        allowSubscription: true,
        allowPerEventPayment: metadata.event_per_event_price_cents > 0,
        allowWaitlist: true,
        perEventPriceCents: metadata.event_per_event_price_cents || null
      },
      
      plans: {
        monthly: { price: 210000, interval: 'month' },
        sixMonth: { price: 1134000, interval: '6_months', discount: 10 },
        yearly: { price: 2142000, interval: 'year', discount: 15 }
      }
    };
  }
  
  console.log('📊 Final subscription data for modal:', subscriptionData);
  
  // 🆕 Open modal with enriched context
  setSubscriptionModal({
    isOpen: true,
    data: subscriptionData
  });
  
  // Reset status so user can interact with modal
  setStatus('idle');
  setMessage('');
  
  return; // Exit early - modal handles next steps
}


  if (result.success) {
    setStatus('success');

    // Handle successful registration with proper status messaging
    if (result.reservation) {
      if (result.reservation.duplicate) {
        // Handle duplicates
        const userStatus = result.reservation.rsvpStatus || 'confirmed';
        if (userStatus === 'waitlisted') {
          setMessage(`✅ You're already on the waitlist for this event! Position #${result.reservation.waitlistPosition || '?'}`);
        } else {
          setMessage(`✅ You're already registered for this event! (${result.reservation.currentCount} people registered)`);
        }
        
        if (onReservationUpdate) {
          onReservationUpdate({
            success: true,
            count: result.reservation.currentCount || 0,
            waitlistCount: result.reservation.waitlistCount || 0,
            capacity: result.reservation.capacity || 25,
            isFull: result.reservation.isFull || false,
            rsvpStatus: result.reservation.rsvpStatus || 'confirmed',
            userReserved: true,
            duplicate: true,
            waitlistPosition: result.reservation.waitlistPosition || null,
            eventSlug: result.reservation.eventSlug
          });
        }
      } else {
        // Handle new registrations
        const userStatus = result.reservation.rsvpStatus || 'confirmed';
        
        if (userStatus === 'waitlisted') {
          setMessage(`🎯 You're on the waitlist! Position #${result.reservation.waitlistPosition || '?'}. You'll be notified if a spot opens.`);
        } else {
          setMessage(`🎉 You're confirmed for this event! Event details will be sent 24 hours beforehand.`);
        }
        
        if (onReservationUpdate) {
          onReservationUpdate({
            success: true,
            count: result.reservation.currentCount || 0,
            waitlistCount: result.reservation.waitlistCount || 0,
            capacity: result.reservation.capacity || 25,
            isFull: result.reservation.isFull || false,
            rsvpStatus: result.reservation.rsvpStatus || 'confirmed',
            userReserved: true,
            duplicate: false,
            waitlistPosition: result.reservation.waitlistPosition || null,
            eventSlug: result.reservation.eventSlug
          });
        }
      }
    } else if (result.reactivated) {
      setIsReactivated(true);
      setMessage('🎉 Welcome back! Your subscription has been reactivated. Check your email for updates.');
      
      await trackEvent('email_reactivation', {
        signup_source: source,
        form_variant: variant,
        ...finalMetadata
      });
    } else {
      setMessage(result.message || '🎉 Welcome to Connect & Co! Check your email for next steps.');
    }
    
    // Track successful signup
    await trackEmailSignup(source, {
      ...finalMetadata,
      reactivated: result.reactivated,
      form_variant: variant,
      signup_successful: true,
      has_reservation: !!result.reservation,
      reservation_count: result.reservation?.currentCount,
      is_duplicate_reservation: result.reservation?.duplicate,
      rsvp_status: result.reservation?.rsvpStatus || 'none'
    });
    
    setEmail('');
    
    if (onSuccess) {
      onSuccess({
        email,
        reservation: result.reservation,
        reactivated: result.reactivated
      });
    }
    
  } else {
    // Handle error cases
    
    // 🆕 ENHANCED: Handle payment requirement responses
    if (result.requiresPayment) {
      setStatus('payment_required');
      setPaymentInfo({
        eventPrice: result.eventPrice,
        currency: result.currency,
        hasSubscription: result.hasSubscription,
        paymentOptions: result.paymentOptions
      });
      setShowPaymentOptions(true);
      setMessage('This event requires payment or subscription');
      return;
    }
    
    setStatus('error');
    
    if (result.isDuplicate) {
      if (result.reservation) {
        const userStatus = result.reservation.rsvpStatus || 'confirmed';
        if (userStatus === 'waitlisted') {
          setMessage(`You're already on the waitlist for this event (Position #${result.reservation.waitlistPosition || '?'})`);
        } else {
          setMessage('You\'re already registered for this event!');
        }
        setStatus('success');
        
        if (onReservationUpdate) {
          onReservationUpdate({
            success: true,
            count: result.reservation.currentCount || 0,
            waitlistCount: result.reservation.waitlistCount || 0,
            capacity: result.reservation.capacity || 25,
            isFull: result.reservation.isFull || false,
            rsvpStatus: result.reservation.rsvpStatus || 'confirmed',
            userReserved: true,
            duplicate: true,
            waitlistPosition: result.reservation.waitlistPosition || null,
            eventSlug: result.reservation.eventSlug
          });
        }
      } else {
        setMessage('You\'re already on our list! Check your email for updates.');
      }
      
      await trackEvent('duplicate_signup_attempt', {
        signup_source: source,
        form_variant: variant,
        ...finalMetadata
      });
    } else {
      // Generic error handling
      setMessage(result.error || 'Something went wrong. Please try again.');
    }
  }

  // Reset status after 5 seconds (except for payment_required)
  if (status !== 'payment_required') {
    setTimeout(() => {
      setStatus('idle');
      setMessage('');
      setIsReactivated(false);
      setShowPaymentOptions(false);
    }, 5000);
  }
};

  // 🔥 ORIGINAL: Keep the original handleSubmit as backup (commented out)
  const orighandleSubmit = async (e, overrideMetadata = null) => {
    if (e) e.preventDefault();

    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');
    setIsReactivated(false);
    setShowPaymentOptions(false);

    // Track form start attempt
    await trackEvent('email_signup_attempt', {
      signup_source: source,
      form_variant: variant,
      page_location: window.location.pathname,
      ...metadata
    });

    // Use override metadata for waitlist flows
    const finalMetadata = overrideMetadata || {
      ...metadata,
      signup_page: window.location.pathname,
      campaign_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
      campaign_medium: new URLSearchParams(window.location.search).get('utm_medium') || 'organic',
      campaign_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || 'none',
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    // Submit email to database
    const result = await emailService.addEmail(email.trim(), source, finalMetadata);

    // 🔥 FIXED: Handle subscription requirement responses
    if (!result.success && result.requiresSubscription) {
      console.log('🚫 Subscription required - redirecting to subscription page');
      
      setStatus('loading');
      setMessage('Redirecting to subscription page...');
      
      // Store email safely
      if (typeof subscriptionService !== 'undefined' && subscriptionService.storeCheckoutEmail) {
        subscriptionService.storeCheckoutEmail(email);
      } else {
        storeEmailForSubscription(email);
      }
      
      // Build subscription URL with event context
      let subscriptionUrl = '/subscription';
      const urlParams = new URLSearchParams();
      urlParams.set('email', email);
      urlParams.set('source', 'event-signup');
      
      if (result.eventInfo?.eventSlug) {
        urlParams.set('event', result.eventInfo.eventSlug);
      }
      
      subscriptionUrl += '?' + urlParams.toString();
      
      // Add a small delay to show the "redirecting" message
      setTimeout(() => {
        window.location.href = subscriptionUrl;
      }, 1000);
      
      return; // Exit early to prevent further processing
    }

    if (result.success) {
      setStatus('success');

      // 🆕 ENHANCED: Handle subscription-aware responses
      if (result.reservation) {
        if (result.reservation.duplicate) {
          // Handle duplicates
          const userStatus = result.reservation.rsvpStatus || 'confirmed';
          if (userStatus === 'waitlisted') {
            setMessage(`✅ You're already on the waitlist for this event! Position #${result.reservation.waitlistPosition || '?'}`);
          } else {
            setMessage(`✅ You're already registered for this event! (${result.reservation.currentCount} people registered)`);
          }
          
          if (onReservationUpdate) {
            onReservationUpdate({
              success: true,
              count: result.reservation.currentCount || 0,
              waitlistCount: result.reservation.waitlistCount || 0,
              capacity: result.reservation.capacity || 25,
              isFull: result.reservation.isFull || false,
              rsvpStatus: result.reservation.rsvpStatus || 'confirmed',
              userReserved: true,
              duplicate: true,
              waitlistPosition: result.reservation.waitlistPosition || null,
              eventSlug: result.reservation.eventSlug
            });
          }
        } else {

          // 🆕 NEW: Handle subscription requirement responses (moved from below)
          if (result.requiresSubscription) {
            setStatus('loading');
            setMessage('Redirecting to subscription page...');
            
            // Store email for subscription process
            subscriptionService.storeCheckoutEmail(email);
            
            // Redirect to subscription page with event context
            const subscriptionUrl = new URL('/subscription', window.location.origin);
            subscriptionUrl.searchParams.set('email', email);
            subscriptionUrl.searchParams.set('source', 'event-signup');
            if (result.eventInfo?.eventSlug) {
              subscriptionUrl.searchParams.set('event', result.eventInfo.eventSlug);
            }
            
            window.location.href = subscriptionUrl.toString();
            return;
          }

          // 🆕 NEW: Handle new registrations with potential payment requirements
          const userStatus = result.reservation.rsvpStatus || 'confirmed';
          
          if (userStatus === 'waitlisted') {
            setMessage(`🎯 You're on the waitlist! Position #${result.reservation.waitlistPosition || '?'}. You'll be notified if a spot opens.`);
          } else {
            setMessage(`🎉 You're confirmed for this event! Event details will be sent 24 hours beforehand.`);
          }
          
          if (onReservationUpdate) {
            onReservationUpdate({
              success: true,
              count: result.reservation.currentCount || 0,
              waitlistCount: result.reservation.waitlistCount || 0,
              capacity: result.reservation.capacity || 25,
              isFull: result.reservation.isFull || false,
              rsvpStatus: result.reservation.rsvpStatus || 'confirmed',
              userReserved: true,
              duplicate: false,
              waitlistPosition: result.reservation.waitlistPosition || null,
              eventSlug: result.reservation.eventSlug
            });
          }
        }
      } else if (result.reactivated) {
        setIsReactivated(true);
        setMessage('🎉 Welcome back! Your subscription has been reactivated. Check your email for updates.');
        
        await trackEvent('email_reactivation', {
          signup_source: source,
          form_variant: variant,
          ...finalMetadata
        });
      } else {
        setMessage(result.message || '🎉 Welcome to Connect & Co! Check your email for next steps.');
      }
      
      // Track successful signup
      await trackEmailSignup(source, {
        ...finalMetadata,
        reactivated: result.reactivated,
        form_variant: variant,
        signup_successful: true,
        has_reservation: !!result.reservation,
        reservation_count: result.reservation?.currentCount,
        is_duplicate_reservation: result.reservation?.duplicate,
        rsvp_status: result.reservation?.rsvpStatus || 'none'
      });
      
      setEmail('');
      
      if (onSuccess) {
        onSuccess({
          email,
          reservation: result.reservation,
          reactivated: result.reactivated
        });
      }
      
    } else {
      // 🆕 ENHANCED: Handle payment requirement responses
      if (result.requiresPayment) {
        setStatus('payment_required');
        setPaymentInfo({
          eventPrice: result.eventPrice,
          currency: result.currency,
          hasSubscription: result.hasSubscription,
          paymentOptions: result.paymentOptions
        });
        setShowPaymentOptions(true);
        setMessage('This event requires payment or subscription');
        return;
      }
      
      setStatus('error');
      
      if (result.isDuplicate) {
        if (result.reservation) {
          const userStatus = result.reservation.rsvpStatus || 'confirmed';
          if (userStatus === 'waitlisted') {
            setMessage(`You're already on the waitlist for this event (Position #${result.reservation.waitlistPosition || '?'})`);
          } else {
            setMessage('You\'re already registered for this event!');
          }
          setStatus('success');
          
          if (onReservationUpdate) {
            onReservationUpdate({
              success: true,
              count: result.reservation.currentCount || 0,
              waitlistCount: result.reservation.waitlistCount || 0,
              capacity: result.reservation.capacity || 25,
              isFull: result.reservation.isFull || false,
              rsvpStatus: result.reservation.rsvpStatus || 'confirmed',
              userReserved: true,
              duplicate: true,
              waitlistPosition: result.reservation.waitlistPosition || null,
              eventSlug: result.reservation.eventSlug
            });
          }
        } else {
          setMessage('You\'re already on our list! Check your email for updates.');
        }
        
        await trackEvent('duplicate_signup_attempt', {
          signup_source: source,
          form_variant: variant,
          ...finalMetadata
        });
      } else {
        setMessage(result.error || 'Something went wrong. Please try again.');
      }
    }

    // Reset status after 5 seconds (except for payment_required)
    if (status !== 'payment_required') {
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
        setIsReactivated(false);
        setShowPaymentOptions(false);
      }, 5000);
    }
  };

  // Style variants (keeping all existing variants unchanged)
  const variants = {
    default: {
      container: "bg-white rounded-2xl shadow-lg p-8",
      title: "text-2xl font-bold text-gray-800 mb-2",
      subtitle: "text-gray-600 mb-6",
      form: "space-y-4",
      input: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all",
      button: "w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    },
    hero: {
      container: "text-center",
      title: "text-3xl md:text-4xl font-bold text-white mb-4",
      subtitle: "text-xl text-white/90 mb-8",
      form: "flex flex-col md:flex-row gap-4 max-w-md mx-auto",
      input: "flex-1 px-4 py-3 rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder-white/70 focus:ring-2 focus:ring-white focus:border-white transition-all backdrop-blur-sm",
      button: "px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    },
    minimal: {
      container: "border border-gray-200 rounded-xl p-6 bg-gray-50",
      title: "text-lg font-semibold text-gray-800 mb-2",
      subtitle: "text-sm text-gray-600 mb-4",
      form: "flex gap-2",
      input: "flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm",
      button: "px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
    },
    vertical: {
      container: "",
      title: "text-lg font-semibold text-gray-800 mb-2",
      subtitle: "text-sm text-gray-600 mb-4 text-center",
      form: "space-y-3",
      input: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all",
      button: "w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
    }
  };

  const styles = variants[variant];

  return (
    <div className={`${styles.container} ${className}`}>
      {title && <h3 className={styles.title}>{title}</h3>}
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      
      {/* 🆕 NEW: Payment Options UI */}
      {showPaymentOptions && paymentInfo && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <h4 className="font-semibold text-blue-900 mb-3">Choose Your Access Option</h4>
          
          <div className="space-y-3">
            {/* Option 1: Pay Per Event */}
             {/* 
            {paymentInfo.paymentOptions?.allowPerEventPayment && (
              <button
                onClick={handlePayPerEvent}
                className="w-full p-3 bg-white border-2 border-blue-300 rounded-lg hover:border-blue-500 transition-colors text-left flex items-center justify-between group"
                disabled={status === 'loading'}
              >
                <div>
                  <div className="font-semibold text-gray-900">Pay for This Event</div>
                  <div className="text-sm text-gray-600">
                    {subscriptionService.formatPrice(paymentInfo.eventPrice, paymentInfo.currency)} - Get immediate access
                  </div>
                </div>
                <CreditCardIcon className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform" />
              </button>
            )}
            */}
             
            
            {/* Option 2: Get Subscription */}
            {paymentInfo.paymentOptions?.allowSubscription && (
              <button
                onClick={handleSubscriptionSignup}
                className="w-full p-3 bg-white border-2 border-purple-300 rounded-lg hover:border-purple-500 transition-colors text-left flex items-center justify-between group"
                disabled={status === 'loading'}
              >
                <div>
                  <div className="font-semibold text-gray-900">Get Monthly Subscription</div>
                  <div className="text-sm text-gray-600">
                    Access all events + priority booking
                  </div>
                </div>
                <div className="text-purple-600 font-bold">⭐</div>
              </button>
            )}
            
            {/* Option 3: Join Waitlist */}
            {paymentInfo.paymentOptions?.allowWaitlist && (
              <button
                onClick={handleWaitlistSignup}
                className="w-full p-3 bg-white border-2 border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-left flex items-center justify-between group"
                disabled={status === 'loading'}
              >
                <div>
                  <div className="font-semibold text-gray-900">Join Waitlist (Free)</div>
                  <div className="text-sm text-gray-600">
                    Get notified if a spot opens up
                  </div>
                </div>
                <div className="text-gray-600">📝</div>
              </button>
            )}
          </div>
          
          <div className="mt-3 text-xs text-blue-700 bg-blue-100 p-2 rounded">
            💡 Subscribers get priority access to all events and skip waitlists
          </div>
        </motion.div>
      )}
      
      {/* Original Form (unchanged when payment options not shown) */}
      {!showPaymentOptions && (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className="relative flex-1">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              className={styles.input}
              disabled={status === 'loading'}
              required
              autoComplete="email"
              spellCheck="false"
            />
          </div>
          
          <motion.button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className={styles.button}
            whileHover={{ scale: status === 'loading' ? 1 : 1.02 }}
            whileTap={{ scale: status === 'loading' ? 1 : 0.98 }}
          >
            {status === 'loading' ? (
              <div className="flex items-center justify-center">
                <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                Joining...
              </div>
            ) : status === 'success' ? (
              <div className="flex items-center justify-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                {isReactivated ? 'Reactivated!' : 'Joined!'}
              </div>
            ) : (
              buttonText
            )}
          </motion.button>
        </form>
      )}

      {/* 🆕 GDPR Consent Text - WHITE COLOR for hero variant */}
      {!showPaymentOptions && (
        <p className={`mt-3 text-xs sm:text-sm text-center leading-relaxed px-2 ${
          variant === 'hero' 
            ? 'text-white/90' 
            : 'text-gray-600'
        }`}>
          By signing up, you agree to our{' '}
         
          <Link 
            to="/community-guidelines" 
            className={`underline font-medium ${
              variant === 'hero'
                ? 'text-white hover:text-white/80'
                : 'text-purple-600 hover:text-purple-700'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
             Community Guidelines
          </Link>,{' '}
          <Link 
            to="/terms" 
            className={`underline font-medium ${
              variant === 'hero'
                ? 'text-white hover:text-white/80'
                : 'text-purple-600 hover:text-purple-700'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </Link>,{' '}
          <Link 
            to="/privacy" 
            className={`underline font-medium ${
              variant === 'hero'
                ? 'text-white hover:text-white/80'
                : 'text-purple-600 hover:text-purple-700'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </Link>, and{' '}
          <Link 
            to="/cookies" 
            className={`underline font-medium ${
              variant === 'hero'
                ? 'text-white hover:text-white/80'
                : 'text-purple-600 hover:text-purple-700'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            Cookie Policy
          </Link>
        </p>
      )}

      {/* Status Message (enhanced to handle payment states) */}
      {message && status !== 'payment_required' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-3 rounded-lg flex items-center text-sm ${
            status === 'success' 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {status === 'success' ? (
            <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
          )}
          {message}
        </motion.div>
      )}

      {/* Trust Indicators (unchanged) */}
      {variant === 'default' && !showPaymentOptions && (
        <div className="mt-6 text-center text-sm text-gray-500">
          <div className="flex items-center justify-center space-x-4">
            <span>✅ No spam</span>
            <span>✅ Unsubscribe anytime</span>
          </div>
        </div>
      )}

      {/* Enhanced success state (unchanged) */}
      {status === 'success' && !showPaymentOptions && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg"
        >
          <h4 className="font-semibold text-purple-800 mb-2">What's Next?</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>📧 Check your email for a welcome message</li>
            <li>🎯 Get priority access to our next events</li>
            <li>🤝 Join our exclusive member community</li>
          </ul>
        </motion.div>
      )}

     
      {/* 🆕 ENHANCED: Subscription Required Modal */}
      <SubscriptionRequiredModal
        isOpen={subscriptionModal.isOpen}
        onClose={() => setSubscriptionModal({ isOpen: false, data: null })}
        subscriptionData={subscriptionModal.data}
        email={email} // 🆕 ADD: User's email for waitlist signup
        eventSlug={subscriptionModal.data?.eventSlug} // 🆕 ADD: Event identifier
        onWaitlistJoin={handleWaitlistFromModal} // Keep existing handler
        onRsvpSuccess={(result) => { // 🆕 ADD: Success callback
          console.log('✅ Waitlist join successful from modal:', result);
          
          // Close the modal
          setSubscriptionModal({ isOpen: false, data: null });
          
          // Update status to success
          setStatus('success');
          
          // Set success message based on RSVP status
          if (result.reservation?.rsvpStatus === 'waitlisted') {
            setMessage(`🎯 You're on the waitlist! Position #${result.reservation.waitlistPosition || '?'}. You'll be notified if a spot opens.`);
          } else {
            setMessage('🎉 Successfully registered for the event!');
          }
          
          // Call the onReservationUpdate callback if it exists
          if (onReservationUpdate && result.reservation) {
            onReservationUpdate({
              success: true,
              count: result.reservation.currentCount || 0,
              waitlistCount: result.reservation.waitlistCount || 0,
              capacity: result.reservation.capacity || 25,
              isFull: result.reservation.isFull || false,
              rsvpStatus: result.reservation.rsvpStatus || 'waitlisted',
              userReserved: true,
              duplicate: false,
              waitlistPosition: result.reservation.waitlistPosition || null,
              eventSlug: result.reservation.eventSlug
            });
          }
          
          // Clear email input
          setEmail('');
          
          // Call onSuccess if provided
          if (onSuccess) {
            onSuccess({
              email: email,
              reservation: result.reservation,
              reactivated: false
            });
          }
        }}
        loading={status === 'loading'}
      />

        {/* 1-29-26 Pass Usage Modal */}
      <PassUsageModal
        isOpen={passUsageModal.isOpen}
        onClose={() => setPassUsageModal({
          isOpen: false,
          email: '',
          passCount: 0,
          eventSlug: '',
          eventTitle: '',
          eventDate: ''
        })}
        onUsePass={handleUsePass}
        onBuySubscription={handleBuySubscriptionFromPass}
        passCount={passUsageModal.passCount}
        eventTitle={passUsageModal.eventTitle}
        eventDate={passUsageModal.eventDate}
        eventSlug={passUsageModal.eventSlug}
        loading={passActionLoading}
      />

    </div>
  );
}

export default EmailSignup;